import jsPDF from "jspdf";
import { toPng } from "html-to-image";

/**
 * Exporta um elemento DOM (tipicamente o container do Dashboard) como PDF multi-página.
 *
 * Estratégia:
 *  1. html-to-image renderiza o elemento em PNG @ 2× (retina, boa qualidade de gráfico)
 *  2. jsPDF cria A4 retrato; largura útil = 190mm (margens laterais 10mm)
 *  3. A imagem é fatiada em páginas: cada página mostra uma janela vertical
 *     da imagem original (canvas offscreen recorta e re-embeda)
 *  4. Header de cada página traz "Dashboard · <empresa>" + timestamp
 *
 * fev/2026 — sem dependência de backend; captura fiel do que o supervisor vê na tela.
 * Filtros aplicados e widget selecionado são preservados no PDF (é o snapshot atual).
 */
export async function exportDashboardPDF(element, { empresaNome = "", periodo = "" } = {}) {
  if (!element) throw new Error("Elemento do Dashboard não encontrado");

  // 1) Renderiza o DOM em PNG (2× dpr — mantém texto e gráficos nítidos)
  const dataUrl = await toPng(element, {
    pixelRatio: 2,
    backgroundColor: "#ffffff",
    cacheBust: true,
    // Skip erros de imagens externas (evitando bloqueio quando alguma quebra)
    skipFonts: false,
    // Filtra elementos que a gente NÃO quer no snapshot (botão de exportar, tabs SA)
    filter: (node) => !node?.dataset?.excludeFromPdf,
  });

  // 2) Carrega PNG num Image pra descobrir dimensões reais
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Falha ao carregar snapshot do Dashboard"));
    i.src = dataUrl;
  });

  // 3) Configura PDF A4 retrato
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();   // 210
  const pageH = pdf.internal.pageSize.getHeight();  // 297
  const margin = 10;
  const headerH = 15;
  const usableW = pageW - margin * 2;               // 190
  const usableH = pageH - margin * 2 - headerH;     // 262

  // Escala mm/px baseada na largura da imagem
  const mmPerPx = usableW / img.width;
  const imgTotalHmm = img.height * mmPerPx;

  // 4) Fatiamento vertical — cada página cobre `usableH` mm da imagem original.
  // Trabalhamos em pixels da imagem original: `sliceHpx` é a altura de cada fatia.
  const sliceHpx = usableH / mmPerPx;
  const totalPages = Math.max(1, Math.ceil(img.height / sliceHpx));
  const now = new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  // Canvas reutilizável para cada fatia
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = Math.ceil(sliceHpx);
  const ctx = canvas.getContext("2d");

  for (let p = 0; p < totalPages; p++) {
    if (p > 0) pdf.addPage();

    // Cabeçalho da página
    pdf.setFillColor(234, 88, 12); // orange-600
    pdf.rect(0, 0, pageW, headerH, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "bold");
    pdf.text(`Dashboard${empresaNome ? " · " + empresaNome : ""}`, margin, 9);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    const rightMeta = `${periodo ? periodo + " · " : ""}gerado em ${now} · pág ${p + 1}/${totalPages}`;
    const rightWidth = pdf.getTextWidth(rightMeta);
    pdf.text(rightMeta, pageW - margin - rightWidth, 9);

    // Fatia atual da imagem original
    const sy = Math.round(p * sliceHpx);
    const sh = Math.min(Math.round(sliceHpx), img.height - sy);
    // Ajusta canvas ao tamanho REAL da fatia (última página pode ser menor)
    canvas.height = sh;
    ctx.clearRect(0, 0, canvas.width, sh);
    ctx.drawImage(img, 0, sy, img.width, sh, 0, 0, img.width, sh);
    const sliceUrl = canvas.toDataURL("image/jpeg", 0.9);

    // Desenha a fatia — altura real em mm considerando `sh` (não `sliceHpx`)
    const sliceHmm = sh * mmPerPx;
    pdf.addImage(sliceUrl, "JPEG", margin, margin + headerH, usableW, sliceHmm, undefined, "FAST");
  }

  // 5) Filename limpo + download nativo do jsPDF
  const nomeSlug = (empresaNome || "empresa").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const dataStr = new Date().toISOString().slice(0, 10);
  pdf.save(`dashboard_${nomeSlug}_${dataStr}.pdf`);
  return { pages: totalPages, imgH: imgTotalHmm };
}
